
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { EtvConfig, PlayerConfig } from '@/interfaces/player-config.interface';
import { logService } from '@/services/log.service';

declare var googletag: any;

@Component
export default class Powerlink extends Vue {
  @Prop({ required: true }) public config!: EtvConfig;
  @Prop({ required: true }) public playerConfig!: PlayerConfig;
  @Prop({ default: 'etv-player' }) public playerId!: string;

  private earthTvText = 'earthTV.com';
  private earthTvLink = 'https://www.earthtv.com?utm_source=player&utm_medium=pl-right&utm_campaign=player-pl';
  private adSlot: any = null;
  private isGoogleTagReady = false;

  get isEnabled(): boolean {
    return !!(this.config?.template?.advertisement?.powerlink?.enabled);
  }

  private mounted() {
    if (this.isEnabled) {
      // Wait for DOM to be fully updated before initializing powerlink
      this.$nextTick(() => {
        this.initializePowerlink();
      });
    }
  }

  @Watch('config.template.advertisement.powerlink.enabled')
  private onPowerlinkEnabledChange(enabled: boolean) {
    if (enabled) {
      this.initializePowerlink();
    } else {
      this.destroyAdSlot();
    }
  }

  private async initializePowerlink() {
    logService.info(`Initializing responsive powerlink for player: ${this.playerId}`);

    try {
      // Light DOM: Google Ad Manager can access elements directly
      this.patchGoogleAdManagerDomAccess();

      // Load Google Tag Manager if not already loaded
      if (typeof googletag === 'undefined') {
        await this.loadGoogleTagManager();
      }

      // Setup responsive ad slot
      this.setupResponsiveAdSlot();
    } catch (error) {
      logService.error('Failed to initialize powerlink:', error);
    }
  }

  private patchGoogleAdManagerDomAccess() {
    // Light DOM: No patching needed - Google Ad Manager can access elements directly
    logService.info('[GAM] Light DOM mode - no getElementById patching required');
  }

  private loadGoogleTagManager(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof googletag !== 'undefined') {
        resolve();
        return;
      }

      // Initialize googletag
      (window as any).googletag = (window as any).googletag || {};
      (window as any).googletag.cmd = (window as any).googletag.cmd || [];

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

      script.onload = () => {
        this.isGoogleTagReady = true;
        logService.info('Google Tag Manager loaded successfully');
        resolve();
      };

      script.onerror = () => {
        logService.error('Failed to load Google Tag Manager');
        reject(new Error('Failed to load Google Tag Manager'));
      };

      document.head.appendChild(script);
    });
  }

  private setupResponsiveAdSlot() {
    if (!this.isGoogleTagReady && typeof googletag === 'undefined') {
      logService.warn('Google Tag Manager not ready, retrying...');
      setTimeout(() => this.setupResponsiveAdSlot(), 100);
      return;
    }

    googletag.cmd.push(() => {
      try {
        // Modern responsive mapping - always include 468x60 and 1x1 px
        const mapping = googletag.sizeMapping()
          .addSize([0, 0], [[1, 1], [300, 50], [320, 50], [468, 60]])        // Mobile: include standard sizes + 468x60 + 1x1
          .addSize([480, 0], [[1, 1], [320, 50], [468, 60]])                 // Small tablet: keep 468x60 + 1x1
          .addSize([768, 0], [[1, 1], [468, 60], [728, 90]])                 // Tablet: always 468x60 + leaderboard + 1x1
          .addSize([1024, 0], [[1, 1], [468, 60], [728, 90], [970, 90]])     // Desktop: always 468x60 + larger sizes + 1x1
          .build();

        // Get account and source info from config
        const accountId = this.config.template?.accountId;
        const sourceId = this.config.source?.id; // Use source.id instead of template.sourceId

        this.adSlot = googletag.defineSlot('/1052246/powerlink_default',
          [[1, 1], [300, 50], [320, 50], [468, 60], [728, 90], [970, 90]], // Always include 1x1 px and 468x60 + other sizes
          `pw_left_${this.playerId}`
        )
        .defineSizeMapping(mapping)
        .setCollapseEmptyDiv(false) // Keep ad space even if no ad loads, so logo stays on right
        .addService(googletag.pubads());

        // Set targeting parameters
        const type = this.playerConfig.live ? 'live' : 'clip';
        googletag.pubads().setTargeting('type', type);
        if (accountId) googletag.pubads().setTargeting('accountId', accountId);
        if (sourceId) googletag.pubads().setTargeting('sourceId', sourceId);
        if (this.playerConfig.adTargetTag) googletag.pubads().setTargeting('targettag', this.playerConfig.adTargetTag);
        googletag.pubads().setTargeting('component', 'playercomponent'); // Distinguish from iframe

        // Enable services
        googletag.pubads().enableSingleRequest();
        googletag.pubads().enableAsyncRendering();
        googletag.enableServices();

        // Wait for DOM element to be available before displaying ad
        this.waitForElementAndDisplay(`pw_left_${this.playerId}`);

        logService.info(`Responsive powerlink ad slot created for player: ${this.playerId}`, {
          accountId,
          sourceId,
          slotId: `pw_left_${this.playerId}`,
          type,
          adTargetTag: this.playerConfig.adTargetTag
        });

      } catch (error) {
        logService.error('Failed to setup responsive ad slot:', error);
      }
    });
  }

  private waitForElementAndDisplay(elementId: string, maxAttempts: number = 50): void {
    let attempts = 0;

    logService.info(`Waiting for DOM element: ${elementId}`);

    const checkElement = () => {
      attempts++;
      // Search within the component's DOM, not the global document
      const element = this.$el.querySelector(`#${elementId}`) || document.getElementById(elementId);

      if (element) {
        // Element found, display the ad using the actual element reference
        logService.info(`DOM element found after ${attempts} attempts: ${elementId}`);
        googletag.cmd.push(() => {
          // Pass the actual element instead of just the ID - this works with Shadow DOM
          googletag.display(element);
          logService.info(`Ad slot displayed successfully using element reference: ${elementId}`);
        });
      } else if (attempts < maxAttempts) {
        // Element not found yet, try again in 50ms
        if (attempts === 1 || attempts % 10 === 0) {
          // Log every 10th attempt to avoid spam
          logService.info(`DOM element not found yet, attempt ${attempts}/${maxAttempts}: ${elementId}`);
          // Debug: show what elements we can find
          const shadowElements = this.$el.querySelectorAll('[id*="pw_left"]');
          const globalElements = document.querySelectorAll('[id*="pw_left"]');
          logService.info(`Shadow DOM elements: ${Array.from(shadowElements).map(el => el.id)}`);
          logService.info(`Global DOM elements: ${Array.from(globalElements).map(el => el.id)}`);
        }
        setTimeout(checkElement, 50);
      } else {
        // Give up after maxAttempts - let's also check what elements DO exist
        const shadowElements = this.$el.querySelectorAll('[id*="pw_left"]');
        const globalElements = document.querySelectorAll('[id*="pw_left"]');
        logService.error(`Failed to find DOM element after ${maxAttempts} attempts: ${elementId}`);
        logService.error(`Shadow DOM elements: ${Array.from(shadowElements).map(el => el.id)}`);
        logService.error(`Global DOM elements: ${Array.from(globalElements).map(el => el.id)}`);
      }
    };

    // Start checking
    checkElement();
  }  private destroyAdSlot() {
    if (!this.adSlot) return;

    googletag.cmd.push(() => {
      try {
        googletag.destroySlots([this.adSlot]);
        this.adSlot = null;
        logService.info(`Powerlink ad slot destroyed for player: ${this.playerId}`);
      } catch (error) {
        logService.warn('Failed to destroy powerlink ad slot:', error);
      }
    });
  }

  private restoreGoogleAdManagerDomAccess() {
    // Restore original document methods
    const doc = document as any;
    if (doc._originalGetElementByIdForAds) {
      document.getElementById = doc._originalGetElementByIdForAds;
      delete doc._originalGetElementByIdForAds;
      logService.info('[GAM] Restored original document methods');
    }
  }

  private beforeDestroy() {
    this.destroyAdSlot();
    this.restoreGoogleAdManagerDomAccess();
  }
}
